import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import { CircularIndeterminate } from "../../../components/common/CircularIndeterminate";
import { MainContentWrapper } from "../../../components/common/Common.styles";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { usePermissionChecker } from "../../../hooks/usePermissionChecker";
import { PermissionEntityType } from "../../../types/permissionType";
import { useProcessingRequest } from "../../../store/selectors/tableManagementSelector";
import { useStatusServiceModule } from "../../../store/selectors/statusServiceModuleSelector";
import { useWalletTypes } from "../../../store/selectors/walletTypesSelector";
import { fetchWalletTypesThunk } from "../../../store/thunk/walletTypesThunk";
import {
    createStatusLevelThunk,
    deleteStatusLevelThunk,
    fetchStatusServiceModuleThunk,
    updateStatusServiceModuleThunk,
    updateStatusLevelThunk,
} from "../../../store/thunk/statusServiceModuleThunk";
import {
    StatusLevel,
    StatusLevelData,
    UpdateStatusServiceModuleData,
} from "../../../types/serviceModuleTypes";
import StatusModuleSettings from "./StatusModuleSettings";
import StatusLevelsTable from "./StatusLevelsTable";
import EditStatusLevelDialog from "./EditStatusLevelDialog";

const StatusPage = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { serviceModuleId } = useParams<{ serviceModuleId: string }>();
    const serviceModuleIdNumber = Number(serviceModuleId);
    const isValidServiceModuleId = !isNaN(serviceModuleIdNumber) && serviceModuleIdNumber > 0;

    const processingRequest = useProcessingRequest();
    const statusModule = useStatusServiceModule();
    const walletTypes = useWalletTypes();
    const { userHasPermission } = usePermissionChecker();
    const canEdit =
        userHasPermission(PermissionEntityType.ServiceModules, "Edit") ||
        userHasPermission(PermissionEntityType.ServiceModules, "Edit", serviceModuleIdNumber);

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editingLevel, setEditingLevel] = useState<StatusLevel | undefined>();

    const breadcrumbs = [
        { title: "serviceModulesPage.serviceModules", link: "serviceModules" },
        { title: "serviceModulesPage.statusPage.status", link: `serviceModules/statussystem/${serviceModuleId}` },
    ];

    useEffect(() => {
        if (!isValidServiceModuleId) return;
        dispatch(fetchStatusServiceModuleThunk(serviceModuleIdNumber));
        dispatch(fetchWalletTypesThunk());
    }, [dispatch, isValidServiceModuleId, serviceModuleIdNumber]);

    const moduleLoaded = statusModule !== null && statusModule.id === serviceModuleIdNumber;
    const levels = moduleLoaded ? statusModule.statusLevels : [];

    const handleSaveSettings = (data: UpdateStatusServiceModuleData) => {
        dispatch(updateStatusServiceModuleThunk({ serviceModuleId: serviceModuleIdNumber, data }));
    };

    const handleSaveLevel = (data: StatusLevelData, id: string | undefined) => {
        if (id) {
            dispatch(
                updateStatusLevelThunk({
                    serviceModuleId: serviceModuleIdNumber,
                    statusLevelId: id,
                    data,
                })
            );
        } else {
            dispatch(createStatusLevelThunk({ serviceModuleId: serviceModuleIdNumber, data }));
        }
    };

    const handleDeleteLevel = (level: StatusLevel) => {
        dispatch(
            deleteStatusLevelThunk({
                serviceModuleId: serviceModuleIdNumber,
                statusLevelId: level.id,
            })
        );
    };

    return (
        <>
            <PageTitle
                breadcrumbs={breadcrumbs}
                title={statusModule?.name || t("serviceModulesPage.statusPage.status")}
            />

            <MainContentWrapper>
                {moduleLoaded && (
                    <>
                        <StatusModuleSettings
                            module={statusModule}
                            walletTypes={walletTypes}
                            canEdit={canEdit}
                            onSave={handleSaveSettings}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 2,
                            }}
                        >
                            <Typography variant="h6">
                                {t("serviceModulesPage.statusPage.levelsTitle")}
                            </Typography>
                            {canEdit && (
                                <GeneralButton
                                    appVariant="primary"
                                    onClick={() => setIsCreateDialogOpen(true)}
                                >
                                    <AddIcon sx={{ mr: 1 }} />
                                    {t("serviceModulesPage.statusPage.createLevelButton")}
                                </GeneralButton>
                            )}
                        </Box>

                        <StatusLevelsTable
                            levels={levels}
                            canEdit={canEdit}
                            handleEditLevel={(level) => setEditingLevel(level)}
                            handleDeleteLevel={handleDeleteLevel}
                        />
                    </>
                )}
            </MainContentWrapper>

            {isCreateDialogOpen && (
                <EditStatusLevelDialog
                    onClose={() => setIsCreateDialogOpen(false)}
                    onSave={handleSaveLevel}
                    siblings={levels}
                />
            )}
            {editingLevel && (
                <EditStatusLevelDialog
                    onClose={() => setEditingLevel(undefined)}
                    onSave={handleSaveLevel}
                    level={editingLevel}
                    siblings={levels.filter((level) => level.id !== editingLevel.id)}
                />
            )}

            <CircularIndeterminate processingRequest={processingRequest} />
        </>
    );
};

export default StatusPage;
